import { useCallback, useEffect, useRef, useState } from "react";
import {
  FileText,
  ImageIcon,
  Loader2,
  MinusCircle,
  PlusCircle,
  UploadCloud,
  Wallet,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import {
  AppDialogBody,
  AppDialogContent,
  AppDialogFooter,
  appDialogField,
  appDialogPrimaryButton,
  appDialogSecondaryButton,
} from "@/components/ui/app-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AsyncSearchableSelect } from "@/components/ui/async-searchable-select";
import { getSelectClients } from "@/Api/clients";
import {
  getBalanceAdjustmentReasons,
  storeBalanceAdjustmentRequest,
} from "@/Api/balanceAdjustmentRequests";
import { Mask } from "@/utils/mask";
import { AdjustmentReason } from "./types";

type Client = { id: number; name: string; phone?: string };

type NewAdjustmentModalProps = {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
};

const ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];

export const NewAdjustmentModal = ({ open, onClose, onCreated }: NewAdjustmentModalProps) => {
  const [client, setClient] = useState<Client | null>(null);
  const [isSubtract, setIsSubtract] = useState(false);
  const [amount, setAmount] = useState("");
  const [reasonId, setReasonId] = useState("");
  const [description, setDescription] = useState("");
  const [previews, setPreviews] = useState<{ file: File; preview: string | null }[]>([]);
  const [reasons, setReasons] = useState<AdjustmentReason[]>([]);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await getBalanceAdjustmentReasons();
        setReasons(data?.response || []);
      } catch (error) {
        console.error("Erro ao carregar motivos:", error);
      }
    })();
  }, [open]);

  const searchClients = useCallback(async (searchTerm: string): Promise<Client[]> => {
    try {
      const isPhoneSearch = /^\d+$/.test(searchTerm.replace(/[\s\-()]/g, ""));
      const { data, ok } = await getSelectClients(
        isPhoneSearch ? { phone: searchTerm } : { name: searchTerm }
      );
      return ok && data?.response ? data.response : [];
    } catch (error) {
      console.error("Erro ao buscar clientes:", error);
      return [];
    }
  }, []);

  const reset = () => {
    previews.forEach((p) => p.preview && URL.revokeObjectURL(p.preview));
    setClient(null);
    setIsSubtract(false);
    setAmount("");
    setReasonId("");
    setDescription("");
    setPreviews([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFiles = (selected: File[]) => {
    const invalid = selected.find((f) => !ALLOWED_TYPES.includes(f.type));
    if (invalid) {
      toast.error(`Arquivo "${invalid.name}" inválido. Use PDF, JPG ou PNG`);
      return;
    }
    setPreviews((prev) => [
      ...prev,
      ...selected.map((file) => ({
        file,
        preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
      })),
    ]);
  };

  const removeFile = (index: number) => {
    const target = previews[index];
    if (target?.preview) URL.revokeObjectURL(target.preview);
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    const value = parseFloat(Mask.transformCurrencyInFloat(amount));

    if (!client) return toast.error("Selecione o cliente.");
    if (Number.isNaN(value) || value <= 0) return toast.error("Informe um valor válido.");
    if (!reasonId) return toast.error("Selecione o motivo.");
    if (!description.trim()) return toast.error("Informe a descrição.");
    if (previews.length === 0) return toast.error("Anexe ao menos um comprovante (PDF, JPG ou PNG).");

    try {
      setLoading(true);
      const { ok } = await storeBalanceAdjustmentRequest({
        client_id: client.id,
        type: isSubtract ? "subtract" : "add",
        amount: value,
        reason_id: Number(reasonId),
        description: description.trim(),
        files: previews.map((p) => p.file),
      });

      // O motivo do erro já é exibido pelo interceptor da API (Api/index.ts)
      if (!ok) return;

      toast.success("Ajuste solicitado! Aguardando aprovação.");
      reset();
      onCreated();
    } catch (error) {
      console.error(error);
      toast.error("Ocorreu um erro ao processar sua solicitação.");
    } finally {
      setLoading(false);
    }
  };

  const typeCard = (active: boolean, tone: "green" | "red") =>
    cn(
      "flex flex-1 items-center justify-center gap-2 rounded-lg border-2 px-4 py-3 text-sm font-semibold transition-colors",
      active
        ? tone === "green"
          ? "border-green-600 bg-green-50 text-green-700 dark:bg-green-950/30"
          : "border-red-600 bg-red-50 text-red-700 dark:bg-red-950/30"
        : tone === "green"
          ? "border-green-200 bg-white text-green-600 hover:bg-green-50 dark:border-green-900 dark:bg-zinc-900"
          : "border-red-200 bg-white text-red-600 hover:bg-red-50 dark:border-red-900 dark:bg-zinc-900"
    );

  return (
    <Dialog open={open} onOpenChange={(value) => !value && handleClose()}>
      <AppDialogContent
        icon={Wallet}
        title="Novo Ajuste de Saldo"
        description="O saldo do cliente só muda depois que o ajuste for aprovado."
      >
        <AppDialogBody>
          <div className="space-y-2">
            <Label>Cliente *</Label>
            <AsyncSearchableSelect<Client>
              fetchItems={searchClients}
              displayField={(item) =>
                item.phone ? `${item.name} - ${Mask.maskPhone(item.phone)}` : item.name
              }
              className={appDialogField}
              placeholder="Buscar cliente..."
              searchPlaceholder="Digite o nome ou telefone do cliente..."
              emptyMessage="Nenhum cliente encontrado."
              value={client}
              onSelect={setClient}
              getItemId={(item) => item.id}
            />
          </div>

          <div className="space-y-2">
            <Label>Tipo de Ajuste</Label>
            <div className="flex gap-3">
              <button
                type="button"
                className={typeCard(!isSubtract, "green")}
                onClick={() => setIsSubtract(false)}
              >
                <PlusCircle className="h-4 w-4" />
                Adicionar
              </button>
              <button
                type="button"
                className={typeCard(isSubtract, "red")}
                onClick={() => setIsSubtract(true)}
              >
                <MinusCircle className="h-4 w-4" />
                Subtrair
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="adjustment-amount">Valor *</Label>
              <Input
                id="adjustment-amount"
                typeMask="currency"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="R$ 0,00"
                className="bg-white dark:bg-zinc-900"
              />
            </div>
            <div className="space-y-2">
              <Label>Motivo *</Label>
              <Select value={reasonId} onValueChange={setReasonId}>
                <SelectTrigger className="bg-white dark:bg-zinc-900">
                  <SelectValue placeholder="Selecione o motivo" />
                </SelectTrigger>
                <SelectContent>
                  {reasons.map((reason) => (
                    <SelectItem key={reason.id} value={String(reason.id)}>
                      {reason.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="adjustment-description">Descrição *</Label>
              <span className="text-xs text-gray-500">{description.length}/1000</span>
            </div>
            <Textarea
              id="adjustment-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descreva o motivo do ajuste de saldo..."
              maxLength={1000}
              rows={3}
              className="resize-none bg-white dark:bg-zinc-900"
            />
          </div>

          <div className="space-y-2">
            <Label>Comprovantes *</Label>
            <input
              ref={fileInputRef}
              id="adjustment-files"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              multiple
              className="hidden"
              onChange={(e) => {
                const selected = Array.from(e.target.files || []);
                if (selected.length > 0) handleFiles(selected);
                if (fileInputRef.current) fileInputRef.current.value = "";
              }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex w-full flex-col items-center gap-1 rounded-lg border-2 border-dashed border-gray-300 bg-white px-4 py-5 text-center transition-colors hover:border-primary hover:bg-primary/5 dark:border-zinc-700 dark:bg-zinc-900"
            >
              <UploadCloud className="h-6 w-6 text-primary" />
              <span className="text-sm font-medium">Clique para anexar arquivos</span>
              <span className="text-xs text-gray-500">PDF, JPG ou PNG</span>
            </button>

            {previews.length > 0 && (
              <ul className="space-y-2">
                {previews.map((item, index) => (
                  <li
                    key={index}
                    className="flex items-center gap-3 rounded-lg border bg-white p-2 dark:bg-zinc-900"
                  >
                    {item.preview ? (
                      <img
                        src={item.preview}
                        alt={item.file.name}
                        className="h-10 w-10 shrink-0 rounded object-cover border"
                      />
                    ) : (
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-red-50 text-red-500">
                        <FileText className="h-5 w-5" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{item.file.name}</p>
                      <p className="flex items-center gap-1 text-xs text-gray-500">
                        {item.preview ? (
                          <ImageIcon className="h-3 w-3" />
                        ) : (
                          <FileText className="h-3 w-3" />
                        )}
                        {(item.file.size / 1024).toFixed(0)} KB
                      </p>
                    </div>
                    <button
                      type="button"
                      className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-zinc-800"
                      onClick={() => removeFile(index)}
                      title="Remover arquivo"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </AppDialogBody>

        <AppDialogFooter>
          <Button
            variant="outline"
            className={appDialogSecondaryButton}
            onClick={handleClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button className={appDialogPrimaryButton} onClick={handleSubmit} disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Solicitar Ajuste
          </Button>
        </AppDialogFooter>
      </AppDialogContent>
    </Dialog>
  );
};
